import { Grid } from '../core/Grid';
import { Attr, Cell, Color, ColorMode, TRANSPARENT } from '../core/types';
import { CSI, DEFAULT_STYLE, RESET, Style, sgrTransition, styleEquals } from './SGR';
import { quantize } from './Palette';

export interface EncodeOptions {
  colorMode: ColorMode;
  /** Emit ESC[2J before the frame. */
  clearScreen: boolean;
  /** Emit ESC[H before the frame. */
  cursorHome: boolean;
  /** Emit ESC[0m at the end. */
  trailingReset: boolean;
  /** Drop unstyled trailing spaces on each line. */
  trimTrailingWhitespace: boolean;
  lineEnding: '\n' | '\r\n';
  /** Background painted into cells that have none of their own. */
  background: Color | null;
  /** Foreground used for cells that have none of their own. */
  foreground: Color | null;
  /** Emit a newline after the final row. */
  finalNewline: boolean;
}

export const DEFAULT_ENCODE_OPTIONS: EncodeOptions = {
  colorMode: 'truecolor',
  clearScreen: false,
  cursorHome: false,
  trailingReset: true,
  trimTrailingWhitespace: true,
  lineEnding: '\n',
  background: null,
  foreground: null,
  finalNewline: false,
};

export interface EncodeStats {
  bytes: number;
  naiveBytes: number;
  escapeSequences: number;
  cells: number;
  savedRatio: number;
}

export interface EncodeResult {
  text: string;
  stats: EncodeStats;
}

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

/** The style a cell should be rendered with, after defaults and quantisation. */
function styleOf(cell: Cell, opts: EncodeOptions): Style {
  const fgRaw = cell.fg === TRANSPARENT ? opts.foreground ?? TRANSPARENT : cell.fg;
  const bgRaw = cell.bg === TRANSPARENT ? opts.background ?? TRANSPARENT : cell.bg;
  return {
    fg: fgRaw === TRANSPARENT ? TRANSPARENT : quantize(fgRaw, opts.colorMode),
    bg: bgRaw === TRANSPARENT ? TRANSPARENT : quantize(bgRaw, opts.colorMode),
    attrs: cell.attrs,
  };
}

function glyph(cell: Cell): string {
  return cell.char === '' ? ' ' : cell.char;
}

/** Attributes that make the foreground colour visible on a blank cell. */
const FG_VISIBLE_ON_SPACE = Attr.UNDERLINE | Attr.STRIKE | Attr.INVERSE;

/**
 * A space shows its background, not its foreground — unless it is underlined,
 * struck through or inverted. Keeping the current foreground across such cells
 * avoids emitting a colour change nobody can see, which matters because
 * designs are full of spaces between coloured runs.
 */
function keepInvisibleForeground(cell: Cell, target: Style, current: Style): Style {
  const isBlank = cell.char === '' || cell.char === ' ';
  if (!isBlank || (target.attrs & FG_VISIBLE_ON_SPACE) !== 0) return target;
  return target.fg === current.fg ? target : { ...target, fg: current.fg };
}

/**
 * Encodes a grid as an ANSI stream.
 *
 * The encoder is a state machine over the active SGR style: a sequence is
 * emitted only when the style actually changes, so a run of same-coloured text
 * costs one escape plus its characters. Trailing runs of unstyled spaces are
 * dropped, which is where most of the size saving on real designs comes from.
 */
export function encodeAnsi(grid: Grid, options: Partial<EncodeOptions> = {}): EncodeResult {
  const opts: EncodeOptions = { ...DEFAULT_ENCODE_OPTIONS, ...options };
  const parts: string[] = [];
  let escapes = 0;

  if (opts.clearScreen) {
    parts.push(`${CSI}2J`);
    escapes += 1;
  }
  if (opts.cursorHome) {
    parts.push(`${CSI}H`);
    escapes += 1;
  }

  let current: Style = { ...DEFAULT_STYLE };

  for (let y = 0; y < grid.height; y++) {
    let lastMeaningful = grid.width - 1;
    if (opts.trimTrailingWhitespace) {
      while (lastMeaningful >= 0) {
        const c = grid.get(lastMeaningful, y);
        const s = styleOf(c, opts);
        const blank = (c.char === '' || c.char === ' ') && s.bg === TRANSPARENT && c.attrs === 0;
        if (!blank) break;
        lastMeaningful -= 1;
      }
    }

    for (let x = 0; x <= lastMeaningful; x++) {
      const cell = grid.get(x, y);
      const target = keepInvisibleForeground(cell, styleOf(cell, opts), current);
      if (!styleEquals(current, target)) {
        const seq = sgrTransition(current, target, opts.colorMode);
        if (seq) {
          parts.push(seq);
          escapes += 1;
        }
        current = target;
      }
      parts.push(glyph(cell));
    }

    const isLast = y === grid.height - 1;
    if (!isLast || opts.finalNewline) {
      // A coloured background must not bleed across the newline.
      if (current.bg !== TRANSPARENT || current.attrs !== 0) {
        parts.push(RESET);
        escapes += 1;
        current = { ...DEFAULT_STYLE };
      }
      parts.push(opts.lineEnding);
    }
  }

  if (opts.trailingReset && parts.length > 0) {
    parts.push(RESET);
    escapes += 1;
  }

  const text = parts.join('');
  const naive = encodeNaive(grid, opts);
  const bytes = byteLength(text);
  const naiveBytes = byteLength(naive);
  return {
    text,
    stats: {
      bytes,
      naiveBytes,
      escapeSequences: escapes,
      cells: grid.length,
      savedRatio: naiveBytes > 0 ? 1 - bytes / naiveBytes : 0,
    },
  };
}

/**
 * Deliberately unoptimised encoding: a full SGR sequence per cell.
 * Only used as the baseline in the optimiser report.
 */
export function encodeNaive(grid: Grid, options: Partial<EncodeOptions> = {}): string {
  const opts: EncodeOptions = { ...DEFAULT_ENCODE_OPTIONS, ...options };
  const parts: string[] = [];
  for (let y = 0; y < grid.height; y++) {
    for (let x = 0; x < grid.width; x++) {
      const cell = grid.get(x, y);
      const s = styleOf(cell, opts);
      parts.push(sgrTransition(DEFAULT_STYLE, s, opts.colorMode) || RESET);
      parts.push(glyph(cell));
    }
    parts.push(RESET);
    if (y < grid.height - 1) parts.push(opts.lineEnding);
  }
  return parts.join('');
}

export interface DiffFrameResult {
  text: string;
  changedCells: number;
  bytes: number;
}

/**
 * Differential frame: repaints only the cells that differ from `prev`.
 *
 * Runs of adjacent changed cells on a row share one cursor-position sequence,
 * which is what keeps a dashboard updating one counter down to a few dozen
 * bytes instead of a full-screen repaint.
 */
export function encodeDiff(
  prev: Grid,
  next: Grid,
  options: Partial<EncodeOptions> = {},
): DiffFrameResult {
  const opts: EncodeOptions = { ...DEFAULT_ENCODE_OPTIONS, ...options };
  const parts: string[] = [];
  let current: Style = { ...DEFAULT_STYLE };
  let changed = 0;

  for (let y = 0; y < next.height; y++) {
    let x = 0;
    while (x < next.width) {
      const a = prev.inBounds(x, y) ? prev.get(x, y) : null;
      const b = next.get(x, y);
      const same =
        a !== null && a.char === b.char && a.fg === b.fg && a.bg === b.bg && a.attrs === b.attrs;
      if (same) {
        x += 1;
        continue;
      }
      // Extend the run while cells keep differing.
      let end = x;
      while (end < next.width) {
        const pa = prev.inBounds(end, y) ? prev.get(end, y) : null;
        const pb = next.get(end, y);
        const eq =
          pa !== null && pa.char === pb.char && pa.fg === pb.fg && pa.bg === pb.bg && pa.attrs === pb.attrs;
        if (eq) break;
        end += 1;
        changed += 1;
      }
      parts.push(`${CSI}${y + 1};${x + 1}H`);
      for (let i = x; i < end; i++) {
        const cell = next.get(i, y);
        const target = keepInvisibleForeground(cell, styleOf(cell, opts), current);
        if (!styleEquals(current, target)) {
          const seq = sgrTransition(current, target, opts.colorMode);
          if (seq) parts.push(seq);
          current = target;
        }
        parts.push(glyph(cell));
      }
      x = end;
    }
  }

  if (parts.length > 0 && opts.trailingReset) parts.push(RESET);
  const text = parts.join('');
  return { text, changedCells: changed, bytes: byteLength(text) };
}

/** Plain-text export, no escapes. */
export function encodeText(grid: Grid, lineEnding: '\n' | '\r\n' = '\n'): string {
  const lines: string[] = [];
  for (let y = 0; y < grid.height; y++) {
    let line = '';
    for (let x = 0; x < grid.width; x++) line += glyph(grid.get(x, y));
    lines.push(line.replace(/\s+$/, ''));
  }
  return lines.join(lineEnding);
}
